import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from './lib/axiosClient';
import useAutoRefresh from './hooks/useAutoRefresh';

const STATUS = {
  draft: { label: 'Draft', color: '#888', icon: '📝' },
  sent: { label: 'Sent', color: '#0984e3', icon: '📤' },
  approved: { label: 'Approved', color: '#00b894', icon: '✅' },
  declined: { label: 'Declined', color: '#d63031', icon: '❌' },
  converted: { label: 'Converted', color: '#6c5ce7', icon: '🔄' },
  expired: { label: 'Expired', color: '#636e72', icon: '⏰' },
};
var fmtPrice = (p) => '$' + parseFloat(p || 0).toFixed(2);
var fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function Estimates() {
  var [estimates, setEstimates] = useState([]);
  var [customers, setCustomers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('active');
  var [search, setSearch] = useState('');
  var [sortBy, setSortBy] = useState('newest');
  var [showModal, setShowModal] = useState(false);
  var [detail, setDetail] = useState(null);

  var fetchData = useCallback(async () => {
    try {
      var [er, cr] = await Promise.all([
        axios.get('/api/estimates'),
        axios.get('/api/customers?limit=500').catch(() => ({ data: { customers: [] } })),
      ]);
      setEstimates(er.data.estimates || []);
      setCustomers(cr.data.customers || cr.data.contacts || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, 15000);

  var openDetail = async (id) => {
    try { var r = await axios.get('/api/estimates/' + id); setDetail(r.data.estimate); }
    catch (e) { alert('Failed'); }
  };
  var saveEstimate = async (form) => {
    try { if (form.id) await axios.put('/api/estimates/' + form.id, form); else await axios.post('/api/estimates', form); setShowModal(false); fetchData(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  var sendEstimate = async (id) => {
    try {
      var res = await axios.post('/api/estimates/' + id + '/send');
      var msgs = [];
      if (res.data.sms_sent) msgs.push('SMS sent');
      if (res.data.email_sent) msgs.push('Email sent');
      alert('✅ Estimate sent!' + (msgs.length ? ' (' + msgs.join(' + ') + ')' : ' (No SMS/email configured)'));
      openDetail(id); fetchData();
    } catch (e) { alert('Failed to send'); }
  };
  var approveEstimate = async (id) => {
    try {
      var res = await axios.put('/api/estimates/' + id, { status: 'approved' });
      if (res.data.work_order) {
        alert('✅ Approved! Work order ' + res.data.work_order.order_number + ' created.');
      } else {
        alert('✅ Estimate approved!');
      }
      openDetail(id); fetchData();
    } catch (e) { alert('Failed'); }
  };
  var convertToInvoice = async (id) => {
    if (!window.confirm('Convert to invoice?')) return;
    try { await axios.post('/api/estimates/' + id + '/convert-to-invoice'); setDetail(null); fetchData(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  var deleteEstimate = async (id) => { if (!window.confirm('Delete?')) return; try { await axios.delete('/api/estimates/' + id); setDetail(null); fetchData(); } catch (e) { alert('Failed'); } };
  var addItem = async (eid, item) => { try { await axios.post('/api/estimates/' + eid + '/items', item); openDetail(eid); fetchData(); } catch (e) { alert('Failed'); } };
  var deleteItem = async (eid, iid) => { try { await axios.delete('/api/estimates/' + eid + '/items/' + iid); openDetail(eid); fetchData(); } catch (e) { alert('Failed'); } };

  // Stats
  var totalValue = estimates.filter(e => ['draft', 'sent'].includes(e.status)).reduce((s, e) => s + parseFloat(e.total || 0), 0);
  var approvedVal = estimates.filter(e => e.status === 'approved').reduce((s, e) => s + parseFloat(e.total || 0), 0);
  var convertedCount = estimates.filter(e => e.status === 'converted').length;

  var filtered = useMemo(() => {
    var q = search.toLowerCase();
    var list = estimates.filter(e => {
      var mf = filter === 'active' ? ['draft', 'sent'].includes(e.status) :
        filter === 'approved' ? e.status === 'approved' :
        filter === 'converted' ? e.status === 'converted' : true;
      var ms = !q || (e.estimate_number || '').toLowerCase().includes(q) || (e.title || '').toLowerCase().includes(q) ||
        (e.contacts?.first_name || '').toLowerCase().includes(q) || (e.contacts?.last_name || '').toLowerCase().includes(q);
      return mf && ms;
    });
    list.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortBy === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sortBy === 'total') return parseFloat(b.total || 0) - parseFloat(a.total || 0);
      return 0;
    });
    return list;
  }, [estimates, filter, search, sortBy]);

  var S = {
    input: { width: '100%', padding: '12px 16px', background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '10px', color: '#e2e2e8', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    label: { fontSize: '13px', fontWeight: '600', color: '#888', marginBottom: '6px', display: 'block' },
    card: { background: '#12121f', border: '1px solid #1e1e35', borderRadius: '16px' },
    pill: (a) => ({ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: a ? '#ff6b2b' : 'transparent', color: a ? '#fff' : '#888' }),
    badge: (c) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', background: c + '20', color: c }),
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#888' }}><p>Loading estimates...</p></div>;

  return (
    <div style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: '0 0 6px' }}>Estimates</h1>
          <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>{estimates.length} total</p>
        </div>
        <button onClick={() => setShowModal(true)} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>+ New Estimate</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Active Value', value: fmtPrice(totalValue), color: '#0984e3' },
          { label: 'Approved', value: fmtPrice(approvedVal), color: '#00b894' },
          { label: 'Converted', value: convertedCount, color: '#6c5ce7' },
          { label: 'Total', value: estimates.length, color: '#ff6b2b' },
        ].map((s, i) => (
          <div key={i} style={{ ...S.card, padding: '16px' }}>
            <p style={{ fontSize: '10px', color: '#666', fontWeight: '700', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</p>
            <p style={{ fontSize: '22px', fontWeight: '800', color: s.color, margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '3px', background: '#1a1c2e', borderRadius: '10px', padding: '4px' }}>
          {['active', 'approved', 'converted', 'all'].map(f => <button key={f} onClick={() => setFilter(f)} style={S.pill(filter === f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>)}
        </div>
        <input placeholder="Search estimate #, title, customer..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...S.input, maxWidth: '260px', flex: '1 1 200px' }} />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...S.input, maxWidth: '160px', cursor: 'pointer' }}>
          <option value="newest">Sort: Newest</option><option value="oldest">Sort: Oldest</option><option value="total">Sort: Total ↓</option>
        </select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 24px', ...S.card }}>
          <p style={{ fontSize: '40px', marginBottom: '12px' }}>📝</p>
          <p style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>{estimates.length === 0 ? 'No estimates yet' : 'No matches'}</p>
          <p style={{ color: '#888', fontSize: '14px' }}>{estimates.length === 0 ? 'Create your first estimate' : 'Try adjusting filters'}</p>
        </div>
      ) : (
        <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1e1e35' }}>
                  {['Estimate', 'Customer', 'Status', 'Total', 'Date', ''].map(h => (
                    <th key={h} style={{ padding: '12px 10px', textAlign: h === 'Total' ? 'right' : 'left', fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(est => {
                  var sc = STATUS[est.status] || STATUS.draft;
                  return (
                    <tr key={est.id} onClick={() => openDetail(est.id)} style={{ borderBottom: '1px solid #1e1e3515', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#252840'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 10px' }}>
                        <span style={{ color: '#6c5ce7', fontWeight: '700', fontSize: '13px' }}>{est.estimate_number}</span>
                        {est.title && <p style={{ color: '#666', fontSize: '11px', margin: '2px 0 0' }}>{est.title}</p>}
                      </td>
                      <td style={{ padding: '12px 10px' }}><span style={{ color: '#aaa', fontSize: '13px' }}>{est.contacts ? est.contacts.first_name + ' ' + est.contacts.last_name : '—'}</span></td>
                      <td style={{ padding: '12px 10px' }}><span style={S.badge(sc.color)}>{sc.icon} {sc.label}</span></td>
                      <td style={{ padding: '12px 10px', textAlign: 'right' }}><span style={{ color: '#fff', fontWeight: '700', fontSize: '14px' }}>{est.total > 0 ? fmtPrice(est.total) : '—'}</span></td>
                      <td style={{ padding: '12px 10px' }}><span style={{ color: '#666', fontSize: '12px' }}>{fmtDate(est.created_at)}</span></td>
                      <td style={{ padding: '12px 10px' }}>
                        <button onClick={e => { e.stopPropagation(); deleteEstimate(est.id); }} style={{ padding: '4px 8px', background: '#d6303110', border: 'none', borderRadius: '6px', color: '#d63031', fontSize: '12px', cursor: 'pointer' }}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 14px', borderTop: '1px solid #1e1e35', fontSize: '12px', color: '#666' }}>Showing {filtered.length} of {estimates.length}</div>
        </div>
      )}

      {showModal && <CreateModal customers={customers} onSave={saveEstimate} onClose={() => setShowModal(false)}
        onCustomerAdded={c => setCustomers(p => [...p, c].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')))} S={S} />}

      {detail && <DetailPanel est={detail} onClose={() => setDetail(null)} onSend={() => sendEstimate(detail.id)}
        onApprove={() => approveEstimate(detail.id)}
        onConvert={() => convertToInvoice(detail.id)} onDelete={() => deleteEstimate(detail.id)}
        onAddItem={item => addItem(detail.id, item)} onDeleteItem={iid => deleteItem(detail.id, iid)} S={S} />}
    </div>
  );
}

// ══════════════════════════════════════════════
// CREATE MODAL
// ══════════════════════════════════════════════
function CreateModal({ customers, onSave, onClose, onCustomerAdded, S }) {
  var [form, setForm] = useState({ contact_id: '', title: '', notes: '', valid_until: '' });
  var [custSearch, setCustSearch] = useState('');
  var [showNewCust, setShowNewCust] = useState(false);
  var [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  var [saving, setSaving] = useState(false);
  var u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  var fc = customers.filter(c => !custSearch || (c.first_name + ' ' + (c.last_name || '') + ' ' + (c.phone || '')).toLowerCase().includes(custSearch.toLowerCase())).slice(0, 12);
  var sel = customers.find(c => String(c.id) === String(form.contact_id));

  useEffect(() => { var d = new Date(); d.setDate(d.getDate() + 30); u('valid_until', d.toISOString().split('T')[0]); }, []);

  var createCustomer = async () => {
    if (!newCust.first_name) return; setSaving(true);
    try { var r = await axios.post('/api/customers', newCust); var c = r.data.customer; if (c && onCustomerAdded) onCustomerAdded(c); u('contact_id', c.id); setShowNewCust(false); setNewCust({ first_name: '', last_name: '', phone: '', email: '' }); }
    catch (e) { alert('Failed'); } setSaving(false);
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ padding: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: 0 }}>New Estimate</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ display: 'grid', gap: '14px' }}>
          <div>
            <label style={S.label}>Customer *</label>
            {sel && !custSearch ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ ...S.input, flex: 1, borderColor: '#00b894', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#fff' }}>{sel.first_name} {sel.last_name}</span><span style={{ color: '#00b894' }}>✓</span>
                </div>
                <button onClick={() => { u('contact_id', ''); setCustSearch(' '); }} style={{ padding: '12px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', cursor: 'pointer', fontSize: '12px' }}>Change</button>
                <button onClick={() => setShowNewCust(!showNewCust)} style={{ padding: '12px', background: showNewCust ? '#ff6b2b' : '#1e1e35', border: 'none', borderRadius: '10px', color: showNewCust ? '#fff' : '#aaa', cursor: 'pointer', fontSize: '12px' }}>{showNewCust ? '✕' : '+ New'}</button>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input placeholder="Search customer..." value={custSearch} onChange={e => setCustSearch(e.target.value)} style={{ ...S.input, flex: 1 }} autoFocus />
                  <button onClick={() => setShowNewCust(!showNewCust)} style={{ padding: '12px', background: showNewCust ? '#ff6b2b' : '#1e1e35', border: 'none', borderRadius: '10px', color: showNewCust ? '#fff' : '#aaa', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>{showNewCust ? '✕' : '+ New'}</button>
                </div>
                <div style={{ maxHeight: '120px', overflowY: 'auto', marginTop: '4px' }}>
                  {fc.map(c => (
                    <div key={c.id} onClick={() => { u('contact_id', c.id); setCustSearch(''); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #1e1e3520' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#252840'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ color: '#fff', fontSize: '13px', fontWeight: '600' }}>{c.first_name} {c.last_name}</span>
                      <span style={{ color: '#666', fontSize: '12px' }}>{c.phone || ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {showNewCust && (
              <div style={{ padding: '14px', background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '10px', marginTop: '8px' }}>
                <p style={{ fontSize: '12px', fontWeight: '700', color: '#ff6b2b', marginBottom: '8px' }}>➕ Quick Add Customer</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <input style={S.input} placeholder="First name *" value={newCust.first_name} onChange={e => setNewCust(p => ({ ...p, first_name: e.target.value }))} />
                  <input style={S.input} placeholder="Last name" value={newCust.last_name} onChange={e => setNewCust(p => ({ ...p, last_name: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <input style={S.input} placeholder="Phone" value={newCust.phone} onChange={e => setNewCust(p => ({ ...p, phone: e.target.value }))} />
                  <input style={S.input} placeholder="Email" value={newCust.email} onChange={e => setNewCust(p => ({ ...p, email: e.target.value }))} />
                </div>
                <button onClick={createCustomer} disabled={saving || !newCust.first_name} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', background: newCust.first_name ? '#00b894' : '#333', color: '#fff', fontWeight: '700', cursor: newCust.first_name ? 'pointer' : 'not-allowed' }}>{saving ? 'Creating...' : '✓ Create & Select'}</button>
              </div>
            )}
          </div>
          <div><label style={S.label}>Title</label><input value={form.title} onChange={e => u('title', e.target.value)} placeholder="e.g. Full service estimate" style={S.input} /></div>
          <div><label style={S.label}>Valid Until</label><input type="date" value={form.valid_until} onChange={e => u('valid_until', e.target.value)} style={S.input} /></div>
          <div><label style={S.label}>Notes</label><textarea value={form.notes} onChange={e => u('notes', e.target.value)} placeholder="Notes for the customer..." rows={2} style={{ ...S.input, resize: 'vertical' }} /></div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '14px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => onSave(form)} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>Create Estimate</button>
        </div>
      </div>
    </Overlay>
  );
}

// ══════════════════════════════════════════════
// DETAIL PANEL
// ══════════════════════════════════════════════
function DetailPanel({ est, onClose, onSend, onApprove, onConvert, onDelete, onAddItem, onDeleteItem, S }) {
  var [newItem, setNewItem] = useState({ type: 'labor', name: '', quantity: '1', unit_price: '' });
  var [showAdd, setShowAdd] = useState(false);
  var sc = STATUS[est.status] || STATUS.draft;
  var typeColors = { labor: '#0984e3', part: '#00b894', fee: '#f59e0b' };

  var printEstimate = () => {
    var w = window.open('', '_blank', 'width=600,height=800'); if (!w) return;
    var items = est.items || [];
    var html = `<html><head><style>
      body{font-family:-apple-system,sans-serif;padding:40px;max-width:560px;margin:0 auto;font-size:13px;color:#222}
      h1{font-size:24px;margin:0 0 4px} hr{border:none;border-top:1px solid #e5e7eb;margin:16px 0}
      table{width:100%;border-collapse:collapse;margin:16px 0} th{text-align:left;border-bottom:2px solid #ddd;padding:8px 4px;font-size:11px;text-transform:uppercase;color:#888}
      td{padding:8px 4px;border-bottom:1px solid #f3f4f6} .right{text-align:right} .bold{font-weight:700}
      .footer{margin-top:32px;text-align:center;color:#888;font-size:11px}
    </style></head><body>`;
    html += `<div style="display:flex;justify-content:space-between"><div><h1>Estimate</h1><div style="color:#888;font-size:12px">${est.estimate_number}</div></div>`;
    html += `<div style="text-align:right"><span style="display:inline-block;padding:4px 12px;border-radius:6px;font-weight:700;font-size:12px;background:${sc.color}20;color:${sc.color}">${sc.label}</span></div></div><hr>`;
    html += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:16px">`;
    html += `<div><div style="font-size:11px;color:#888;text-transform:uppercase;font-weight:700">Customer</div><div style="font-weight:700;margin-top:4px">${est.contacts?.first_name || ''} ${est.contacts?.last_name || ''}</div>`;
    if (est.contacts?.phone) html += `<div style="color:#666">${est.contacts.phone}</div>`;
    html += `</div><div style="text-align:right"><div style="color:#888;font-size:12px">Date: ${fmtDate(est.created_at)}</div>`;
    if (est.valid_until) html += `<div style="color:#f59e0b;font-weight:700;font-size:12px">Valid until: ${fmtDate(est.valid_until)}</div>`;
    html += `</div></div>`;
    if (est.title) html += `<div style="font-weight:700;font-size:16px;margin-bottom:12px">${est.title}</div>`;
    html += `<table><thead><tr><th>Item</th><th>Type</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Total</th></tr></thead><tbody>`;
    items.forEach(i => { html += `<tr><td class="bold">${i.name}</td><td>${i.type}</td><td class="right">${i.quantity}</td><td class="right">${fmtPrice(i.unit_price)}</td><td class="right bold">${fmtPrice(i.total)}</td></tr>`; });
    html += `</tbody></table><hr>`;
    html += `<div style="max-width:220px;margin-left:auto">`;
    if (parseFloat(est.subtotal || 0) > 0) html += `<div style="display:flex;justify-content:space-between;padding:4px 0"><span>Subtotal</span><span>${fmtPrice(est.subtotal)}</span></div>`;
    if (parseFloat(est.tax_amount || 0) > 0) html += `<div style="display:flex;justify-content:space-between;padding:4px 0"><span>Tax</span><span>${fmtPrice(est.tax_amount)}</span></div>`;
    html += `<div style="display:flex;justify-content:space-between;padding:8px 0;border-top:2px solid #222;font-weight:800;font-size:18px"><span>Total</span><span>${fmtPrice(est.total)}</span></div></div>`;
    if (est.notes) html += `<div style="margin-top:16px;padding:12px;background:#f8f8f8;border-radius:8px;font-size:12px"><strong>Notes:</strong> ${est.notes}</div>`;
    html += `<div style="margin-top:40px;width:200px;border-top:1px solid #888;padding-top:4px;font-size:11px;color:#888">Customer Approval</div>`;
    html += `<div class="footer">This estimate is valid for 30 days</div></body></html>`;
    w.document.write(html); w.document.close(); setTimeout(() => w.print(), 300);
  };

  return (
    <Overlay onClose={onClose}>
      <div>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <span style={{ color: '#6c5ce7', fontWeight: '800', fontSize: '15px' }}>{est.estimate_number}</span>
              <span style={S.badge(sc.color)}>{sc.icon} {sc.label}</span>
            </div>
            <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 4px' }}>{est.title || 'Untitled Estimate'}</h3>
            {est.contacts && <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>{est.contacts.first_name} {est.contacts.last_name}{est.contacts.phone ? ' · ' + est.contacts.phone : ''}</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '0 24px 24px' }}>
          {/* Totals */}
          {parseFloat(est.total || 0) > 0 && (
            <div style={{ background: '#1a1c2e', borderRadius: '14px', padding: '18px', margin: '16px 0', border: '1px solid #1e1e35' }}>
              {parseFloat(est.subtotal || 0) > 0 && <R label="Subtotal" value={fmtPrice(est.subtotal)} />}
              {parseFloat(est.tax_amount || 0) > 0 && <R label="Tax" value={fmtPrice(est.tax_amount)} />}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '2px solid #1e1e35', marginTop: '4px' }}>
                <span style={{ color: '#fff', fontSize: '16px', fontWeight: '800' }}>Estimated Total</span>
                <span style={{ color: '#6c5ce7', fontSize: '24px', fontWeight: '800' }}>{fmtPrice(est.total)}</span>
              </div>
            </div>
          )}

          {/* Dates */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', fontSize: '12px' }}>
            <span style={{ color: '#888' }}>Created: <strong style={{ color: '#aaa' }}>{fmtDate(est.created_at)}</strong></span>
            {est.valid_until && <span style={{ color: '#888' }}>Valid until: <strong style={{ color: new Date(est.valid_until) < new Date() ? '#d63031' : '#aaa' }}>{fmtDate(est.valid_until)}</strong></span>}
          </div>

          {/* Line Items */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ color: '#fff', fontSize: '13px', fontWeight: '700', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Line Items</h4>
              {est.status === 'draft' && <button onClick={() => setShowAdd(!showAdd)} style={{ padding: '4px 12px', background: '#1e1e35', border: 'none', borderRadius: '6px', color: '#ff6b2b', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>{showAdd ? 'Cancel' : '+ Add Item'}</button>}
            </div>

            {showAdd && (
              <div style={{ background: '#1a1c2e', borderRadius: '10px', padding: '12px', marginBottom: '10px', border: '1px dashed #1e1e35' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '8px', marginBottom: '8px' }}>
                  <select value={newItem.type} onChange={e => setNewItem(p => ({ ...p, type: e.target.value }))} style={{ ...S.input, padding: '8px', fontSize: '12px' }}>
                    <option value="labor">Labor</option><option value="part">Part</option><option value="fee">Fee</option>
                  </select>
                  <input placeholder="Item name" value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} style={{ ...S.input, padding: '8px', fontSize: '12px' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px' }}>
                  <input type="number" placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem(p => ({ ...p, quantity: e.target.value }))} style={{ ...S.input, padding: '8px', fontSize: '12px' }} />
                  <input type="number" placeholder="Price" value={newItem.unit_price} onChange={e => setNewItem(p => ({ ...p, unit_price: e.target.value }))} style={{ ...S.input, padding: '8px', fontSize: '12px' }} />
                  <button onClick={() => { onAddItem(newItem); setNewItem({ type: 'labor', name: '', quantity: '1', unit_price: '' }); setShowAdd(false); }}
                    style={{ padding: '8px 16px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>Add</button>
                </div>
              </div>
            )}

            {(est.items || []).length === 0 && !showAdd && <p style={{ color: '#555', fontSize: '13px', textAlign: 'center', padding: '16px', fontStyle: 'italic' }}>No items yet</p>}

            {(est.items || []).map(item => {
              var tc = typeColors[item.type] || '#888';
              return (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1e1e3520' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', background: tc + '20', color: tc }}>{item.type}</span>
                    <span style={{ color: '#fff', fontSize: '13px', fontWeight: '600' }}>{item.name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: '#666', fontSize: '12px' }}>{item.quantity} × {fmtPrice(item.unit_price)}</span>
                    <span style={{ color: '#00b894', fontWeight: '700', fontSize: '13px', minWidth: '60px', textAlign: 'right' }}>{fmtPrice(item.total)}</span>
                    {est.status === 'draft' && <button onClick={() => onDeleteItem(item.id)} style={{ width: '20px', height: '20px', background: '#d6303115', border: 'none', borderRadius: '4px', color: '#d63031', cursor: 'pointer', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>}
                  </div>
                </div>
              );
            })}
          </div>

          {est.notes && <div style={{ marginBottom: '16px' }}><h4 style={{ color: '#888', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>Notes</h4><p style={{ color: '#aaa', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>{est.notes}</p></div>}

          {/* Public approval link */}
          {['sent', 'approved'].includes(est.status) && (
            <div style={{ background: '#0984e310', border: '1px solid #0984e330', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', color: '#0984e3', fontWeight: '700', marginBottom: '6px' }}>CUSTOMER APPROVAL LINK</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input readOnly value={'https://ivisionmarketing.com/estimate/' + est.estimate_number}
                  style={{ flex: 1, background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '8px', padding: '8px 10px', color: '#0984e3', fontSize: '12px', fontFamily: 'monospace' }} />
                <button onClick={() => { navigator.clipboard.writeText('https://ivisionmarketing.com/estimate/' + est.estimate_number); }}
                  style={{ padding: '8px 12px', background: '#0984e3', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '700', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>📋 Copy</button>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={onDelete} style={{ padding: '10px 16px', background: '#d6303115', border: 'none', borderRadius: '10px', color: '#d63031', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>Delete</button>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={printEstimate} style={{ padding: '10px 16px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>🖨️ Print</button>
            {est.status === 'draft' && <button onClick={onSend} style={{ padding: '10px 16px', background: '#0984e3', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>📤 Send to Customer</button>}
            {est.status === 'sent' && <button onClick={onApprove} style={{ padding: '10px 16px', background: '#00b894', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>✅ Approve</button>}
            {['draft', 'sent', 'approved'].includes(est.status) && <button onClick={onConvert} style={{ padding: '10px 16px', background: '#6c5ce7', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>🔄 Convert to Invoice</button>}
          </div>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '30px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#12121f', borderRadius: '20px', border: '1px solid #1e1e35', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>{children}</div>
    </div>
  );
}

function R({ label, value }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#888', fontSize: '13px' }}>{label}</span><span style={{ color: '#aaa', fontSize: '13px', fontWeight: '600' }}>{value}</span></div>;
}
